import type { CustomFieldValues } from '../custom-fields/contract';
import type { ContactPhone, CustomerType, OriginChannel } from '../customers/contract';

/**
 * Contacts: the people of the CRM (docs/specs/033-contatos/spec.md): every number that opened a
 * conversation, every person of an address book, every person of a customer. A contact may
 * belong to a customer or stand alone ("solto"); the list shows both, saying which customer.
 */

export const CONTACT_LIMITS = {
  nameMin: 2,
  nameMax: 160,
  noteMax: 500,
  searchMax: 100,
} as const;

/** `ownerMembershipId=none`: the contacts without an owner. */
export const NO_OWNER = 'none';

export interface ContactOwner {
  membershipId: string;
  name: string;
}

export interface ContactUnit {
  id: string;
  name: string;
}

/** `ContactDto` of the API (`GET /contacts`). */
export interface LooseContact {
  id: string;
  /** Null = a contact without a customer. */
  customerId: string | null;
  name: string;
  jobTitle: string | null;
  isPrimary: boolean;
  phones: ContactPhone[];
  emails: string[];
  instagram: string | null;
  /** PF/PJ, the company the person speaks for and its CPF/CNPJ (spec 071). */
  type: CustomerType;
  company: string | null;
  document: string | null;
  customFields: CustomFieldValues;
  ownerMembershipId: string | null;
  unitId: string | null;
  originChannel: OriginChannel | null;
  originDetail: string | null;
  note: string | null;
  archivedAt: string | null;
  /** The customer the contact belongs to; null = a loose contact. */
  customer: { id: string; name: string } | null;
  /** A contact of a customer has the owner and the unit of its customer. */
  owner: ContactOwner | null;
  unit: ContactUnit | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContactList {
  items: LooseContact[];
  total: number;
  page: number;
  pageSize: number;
}

/** `with`: the contacts of a customer; `without`: the loose ones. */
export type ContactLink = 'with' | 'without';

export interface ContactListQuery {
  q?: string;
  link?: ContactLink;
  /** A member's id, or `none`. */
  ownerMembershipId?: string;
  unitId?: string;
  archived?: boolean;
  page?: number;
  pageSize?: number;
}

/** The body of `POST /contacts` (the caller owns the contact unless `ownerMembershipId` says). */
export interface ContactInput {
  name: string;
  jobTitle?: string | null;
  phones?: ContactPhone[];
  emails?: string[];
  instagram?: string | null;
  ownerMembershipId?: string | null;
  unitId?: string;
  note?: string | null;
  type?: CustomerType;
  company?: string | null;
  document?: string | null;
  /** Where the person came from (a shared contact: WhatsApp, "Compartilhado por …"). */
  origin?: { channel: OriginChannel | null; detail?: string | null };
}
